// ABOUTME: The cloud stack's colors: each feeling's soft fill mixed toward slate, tier by tier, plus the unrevealed greys.
// ABOUTME: A pure function of the feeling code; the formula and values come from the Cloud Color Progression handoff.

import { CROSSING_NEUTRAL_FILL } from '../../../../theme/crossingTokens';

export const FEELING_CODES =['happy', 'excited', 'surprised', 'scared', 'sad', 'worried', 'yucky', 'angry'] as const;

export interface CloudPalette {
  base: string;
  lower: string;
  upper: string;
  head: string;
  b1: string;
  b2: string;
  b3l: string;
  b3r: string;
  b4l: string;
  b4r: string;
}

const SLATE = '#7E8795';
const WHITE = '#FFFFFF';

const SOFT_FILLS: Record<string, string> = {
  happy: '#F3E5B2',
  excited: '#F3D1AA',
  surprised: '#C7E4E3',
  scared: '#CFD1E1',
  sad: '#CCDCE7',
  worried: '#DCD3E6',
  yucky: '#D6DEC9',
  angry: '#EFC5BE',
};

// The olive source drifts muddy when mixed, so Yucky is hand-tuned.
const YUCKY_PALETTE: CloudPalette = {
  base: '#CBD6C1', lower: '#ADB8B2', upper: '#99A3AB', head: SLATE,
  b1: '#D4DECB', b2: '#B7C2B6', b3l: '#ACB6B1', b3r: '#9FA9AC', b4l: '#939CA4', b4r: '#86909D',
};

// Shown for a tier whose answer is not yet given.
export const PLACEHOLDER_PALETTE: CloudPalette = {
  base: '#E9ECEA', lower: '#E9ECEA', upper: '#DFE3E1', head: '#E4E7E3',
  b1: '#E9ECE9', b2: '#E9ECE9', b3l: '#E6E9E7', b3r: '#E2E6E4', b4l: '#E9ECE9', b4r: '#E6E9E6',
};

const channel = (hex: string, offset: number) => parseInt(hex.slice(offset, offset + 2), 16);

export function mixHex(a: string, b: string, t: number): string {
  const mixed = [1, 3, 5].map((offset) => {
    const from = channel(a, offset);
    const to = channel(b, offset);
    return Math.round(from + (to - from) * t).toString(16).padStart(2, '0');
  });
  return `#${mixed.join('').toUpperCase()}`;
}

function mixedPalette(soft: string): CloudPalette {
  const base = mixHex(soft, SLATE, 0.12);
  const lower = mixHex(soft, SLATE, 0.45);
  const upper = mixHex(soft, SLATE, 0.67);
  const head = SLATE;
  return {
    base, lower, upper, head,
    b1: mixHex(base, WHITE, 0.17),
    b2: mixHex(base, lower, 0.68),
    b3l: mixHex(lower, upper, 0.05),
    b3r: mixHex(lower, upper, 0.72),
    b4l: mixHex(upper, head, 0.26),
    b4r: mixHex(upper, head, 0.67),
  };
}

const PALETTES: Record<string, CloudPalette> = Object.fromEntries(
  Object.entries(SOFT_FILLS).map(([code, soft]) => [code, code === 'yucky' ? YUCKY_PALETTE : mixedPalette(soft)]),
);

const warned = new Set<string>();

const NEUTRAL_PALETTE = mixedPalette(CROSSING_NEUTRAL_FILL);

const own = <T>(table: Record<string, T>, code: string): T | undefined => (Object.prototype.hasOwnProperty.call(table, code) ? table[code] : undefined);

export function paletteFor(code: string): CloudPalette {
  const palette = own(PALETTES, code);
  if (palette) return palette;
  if (!warned.has(code)) {
    warned.add(code);
    console.warn(`Crossing cloud palette has no entry for feeling "${code}"; drawing the neutral fill.`);
  }
  return NEUTRAL_PALETTE;
}
